import { useEffect, useState } from 'react';

/**
 * True while `query` matches (CSS media query, e.g. `(orientation: portrait)`).
 *
 * Layout decisions that CSS alone cannot make - a third-party grid's column list, a card vs.
 * table render - go through this rather than user-agent sniffing. `fallback` is used where
 * `matchMedia` is missing (jsdom, SSR).
 */
export function useMediaQuery(query: string, fallback = false): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : fallback,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

/**
 * Tablet-width touch layouts (iPad 820-1180px either way round) - below the `lg`-ish desktop
 * table layouts but above phones. Pointer-based so a narrow desktop window keeps the desktop UI.
 */
export const TABLET_TOUCH_QUERY = '(min-width: 768px) and (max-width: 1366px) and (pointer: coarse)';

export default useMediaQuery;
